/**
 * "Sign out everywhere" — `POST /api/auth/logout-all` (PIVOT-113), on the
 * profile page next to account erasure (`DeleteAccount`). Revokes every
 * session token this user has ever been issued by bumping
 * `users.token_version` server-side, including the token authenticating this
 * very call, so this device is signed out too.
 *
 * Unlike `DeleteAccount`, nothing here is irreversible — signing back in
 * undoes it completely — so there is no two-step confirmation, just a single
 * button and honest feedback either way.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router'
import { logoutAll } from '../api/auth'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n'
import './SignOutEverywhere.css'

export function SignOutEverywhere() {
  const { t } = useI18n()
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(false)

  const handleClick = async () => {
    if (submitting) return
    setSubmitting(true)
    setError(false)
    try {
      await logoutAll()
    } catch {
      // The backend never bumped the version, so every existing session --
      // including this one -- is still good. Say so and stay signed in.
      setError(true)
      setSubmitting(false)
      return
    }
    // This tab's own token is revoked too (that is the point of the call),
    // so drop it locally now rather than waiting for the honest-401 path to
    // catch it on the next click.
    signOut()
    void navigate('/', { replace: true })
  }

  return (
    <section className="sign-out-everywhere">
      <h3>{t('profile.signOutEverywhereTitle')}</h3>
      <p className="sign-out-everywhere-body">{t('profile.signOutEverywhereBody')}</p>
      {error && <p className="form-error">{t('profile.signOutEverywhereFailed')}</p>}
      <button
        type="button"
        className="btn btn-outline"
        disabled={submitting}
        onClick={() => void handleClick()}
      >
        {submitting ? t('common.loading') : t('profile.signOutEverywhereButton')}
      </button>
    </section>
  )
}
